import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import IconSend from '@assets/images/icons/send.svg'
import SpinnerIcon from '@assets/images/icons/loader.svg'
import clsx from 'clsx'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useSelector } from 'react-redux'
import TodoMessage from './todo-message'
import TodoService from '@/modules/todo/todoService'
import TextInput, { VARIANT_LIGHT } from '@/modules/base/components/inputs/text-input/text-input'
import buildName from '../_helpers/buildName'
import Avatar from '@/modules/base/components/avatar/avatar'
import lang from '@/lang'

const useStyles = makeStyles(theme => ({
  commentZone: {
    paddingTop: 15,
    background: theme.backgroundColor,
    '& > div': {
      margin: '0px 34px',
    },
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 20,
  },
  title: {
    color: theme.textLighter,
    textTransform: 'uppercase',
    fontSize: 13,
    letterSpacing: '0.13px',
    paddingBottom: 10,
    borderBottom: `1px solid ${theme.borderColor}`,
  },
  historyListScroll: {
    margin: '0 !important',
  },
  historyList: {
    padding: '0px 34px',
  },
  message: {
    paddingTop: 15,
    paddingBottom: 15,
    borderBottom: theme.borderColor,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  textInputContainer: {
    paddingTop: 15,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  messageInput: {
    marginLeft: 5,
    cursor: 'pointer',
    '& input:focus + div': {
      background: `${theme.primary} !important`,
      borderColor: theme.primary,
      '&:hover': {
        background: `${theme.primaryLight} !important`,
        borderColor: theme.primaryLight,
      },
      '& svg path': {
        fill: '#fff',
        stroke: '#fff',
      },
    },
  },
  messageInputSending: {
    marginLeft: 5,
    '& svg path': {
      fill: theme.primary,
      transformOrigin: 'center',
      animation: 'spin 450ms linear infinite',
    },
  },
  messageInputRequired: {
    borderColor: 'red',
  },
  loadMore: {
    color: theme.primary,
    cursor: 'pointer',
    border: '0 none',
    width: '100%',
    textAlign: 'center',
    background: 'transparent',
  },
}), { name: 'TodoMessages' })

const TodoMessages = (props) => {
  const {
    todo: todoProp,
    className,
    readOnly,
    onPreviewFile,
    showTitle,
    limit,
    styles,
  } = props
  const todo = todoProp || {}

  const [pagination, setPagination] = useState({ offset: 0, limit })
  const [messageItems, setMessageItems] = useState({ history: [], count: 0 })
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [showMessageRequired, setShowMessageRequired] = useState()
  const classes = useStyles()
  const user = useSelector(state => state.user.info)
  const userName = useMemo(() => {
    return buildName(user)
  }, [user])

  useEffect(() => {
    if (pagination.offset > 0 && todo.id) {
      TodoService.getMessages(todo.id, pagination).then(({ data, meta: { count } }) => {
        setMessageItems(previous => {
          return { history: data.reverse().concat(previous.history), count }
        })
      })
    }
  }, [pagination])

  useEffect(() => {
    if (todo.id) {
      TodoService.getMessages(todo.id, pagination).then(({ data, meta: { count } }) => {
        setMessageItems(previous => {
          return { history: data.reverse(), count }
        })
      })
    }
  }, [todo])

  const loadNext = () => {
    setPagination(previous => { return { ...previous, offset: previous.offset + limit } })
  }

  const onMessageChange = value => {
    setNewMessage(value)
    setShowMessageRequired(value.length === 0)
  }

  const addMessage = () => {
    if (isSending) {
      return
    }
    if (newMessage.length) {
      setIsSending(true)
      TodoService.createMessage(todo.id, newMessage).then(({ data: createdMessage }) => {
        setMessageItems(previous => {
          if (previous.history.length > limit) {
            previous.history.shift()
          }
          previous.history.push(createdMessage)
          return { history: previous.history, count: previous.count + 1 }
        })
        setNewMessage('')
        return TodoService.markAsSeen(todo, false)
      }).finally(() => {
        setIsSending(false)
      })
    } else {
      setShowMessageRequired(true)
    }
  }

  const deleteMessage = (todoId, messageId) => {
    TodoService.deleteMessage(todoId, messageId)
      .then(() => {
        setMessageItems(previous => {
          const index = previous.history.findIndex(entry => entry.id === messageId)
          const newHistory = [...previous.history]
          newHistory[index].deletedAt = new Date()
          return { history: newHistory }
        })
      })
  }

  const historyList = messageItems.history.map(message =>
    (<TodoMessage
      key={`M${message.id}`}
      message={message}
      todo={todo}
      className={classes.message}
      onDelete={deleteMessage}
      onPreviewFile={onPreviewFile}
      readOnly={readOnly}
    />),
  )

  if (!todoProp) {
    return null
  }

  const moreItemsCount = messageItems.count - messageItems.history.length
  const loadMore = moreItemsCount > 0
    ? (<button onClick={loadNext} className={classes.loadMore}> {lang.todo.messages.loadMore(moreItemsCount)}</button>)
    : null
  return (
    <div className={clsx(classes.commentZone, className)}>
      {showTitle && <div className={classes.title}>{lang.todo.messages.title}</div>}
      {loadMore}
      {historyList.length > 0 && (
        <PerfectScrollbar className={classes.historyListScroll}>
          <div className={classes.historyList}>
            {historyList}
          </div>
        </PerfectScrollbar>
      )}
      {!readOnly && (
        <div className={clsx(classes.textInputContainer, styles.input && styles.input)}>
          <Avatar kind='name' salutation={userName} width='20px' />
          <TextInput
            name='commentMessage'
            placeholder={lang.todo.messages.inputPlaceHolder}
            value={newMessage}
            onChange={onMessageChange}
            onEnter={addMessage}
            useFormik={false}
            disabled={readOnly || isSending}
            className={clsx(
              isSending ? classes.messageInputSending : classes.messageInput,
              showMessageRequired && classes.messageInputRequired)}
            icon={isSending ? SpinnerIcon : IconSend}
            iconPosition='after'
            autoComplete='off'
            onIconClick={addMessage}
            variant={VARIANT_LIGHT}
          />
        </div>
      )}
    </div>
  )
}

TodoMessages.propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  todo: PropTypes.object,
  limit: PropTypes.number,
  showTitle: PropTypes.bool,
  styles: PropTypes.shape({
    input: PropTypes.string,
  }),
}

TodoMessages.defaultProps = {
  className: null,
  readOnly: false,
  showTitle: true,
  limit: 3,
  styles: {},
}

export default TodoMessages
